import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';

import uniq from 'lodash/uniq';

import { formatAssayDetailsDate, formatAssayDetailsDatetime } from '../../../utils/date_utils';

import Button from '../../common/Button/Button';
import CardView from '../../common/CardView/CardView';
import CaretDownIcon from '../../common/icons/CaretDownIcon';
import CaretUpIcon from '../../common/icons/CaretUpIcon';
import Icon from '../../common/icons/Icon';
import Label from '../../common/Labels/Label';
import LinkButton from '../../common/Labels/LinkButton';
import ModalOverlay from '../../common/ModalOverlay/ModalOverlay';
import ScrollableBox from '../../common/overlays/ScrollableBox';

import { calculateNextRun } from '../../../utils/assay_utils';
import { getThemeColor } from '../../../utils/theme-utils';
import { sortEdgeLocations } from '../../edge/edge_utils';

import { WALLAROO_OPS_CENTER_REGEX } from '../../../constants/wallaroo.constants';
import useClientContext from '../../contexts/ClientData/ClientData';
import getNextRun from '../../../utils/api_functions/getNextRun';

export const AssayDetailAttribute = ({
  label,
  children,
  icon = null,
  useTailwindSizing = false,
}) => (
  <div className="flex flex-col items-start">
    <Label bold>{label}</Label>
    <div className="flex justify-center items-center">
      {
        icon && (
          <Icon className="mr-2" fill={getThemeColor('brand')} icon={icon} useTailwindSizing={useTailwindSizing} size={24} />
        )
      }
      <Label>{children}</Label>
    </div>
  </div>
)

const getBaselineRangeString = (baselineStart, baselineEnd) => `${formatAssayDetailsDate(baselineStart)} >> ${formatAssayDetailsDate(baselineEnd)}`

const areBinsEquallyWeighted = (binWeights = []) => binWeights.every(value => value === binWeights[0]);

const ScrollSectionWrapper = ({ label }) => (
  <div className="m-2">
    <ScrollableBox>
      <div style={{ width: '544px', maxHeight: '64px' }}>
        <Label>{label}</Label>
      </div>
    </ScrollableBox>
  </div>
)

const getLocationPreviewString = (locationNames = [], hasAllEdges = false) => {
  if (hasAllEdges) {
    return 'All Edges';
  }

  if (!locationNames?.length) {
    return 'No Edges';
  }
  if (locationNames.length === 1) {
    return locationNames[0];
  }
  return `${locationNames.length} Locations`;
}

const CollapsibleCaret = ({ showNames = false, showCollapsibleSection = false }) => {
  if (!showCollapsibleSection) {
    return null;
  }

  return showNames ? <CaretDownIcon /> : <CaretUpIcon />
}

const CollapsibleLocationsSection = ({ locationNames, hasAllEdges = false }) => {
  const [showNames, setShowNames] = useState(false);
  const locationCount = locationNames?.length || 0;
  const locationNamesLabel = locationNames?.join(', ');
  const locationPreviewLabel = getLocationPreviewString(locationNames, hasAllEdges);

  const showCollapsibleSection = locationCount > 1;
  return (
    <div className="mt-2 flex flex-col items-start w-full">
      <Label bold>Locations</Label>
      <LinkButton onClick={() => setShowNames(!showNames)}>
        <div className="flex">
          <Label className="mr-2">{locationPreviewLabel}</Label>
          <CollapsibleCaret showNames={showNames} showCollapsibleSection={showCollapsibleSection} />
        </div>
      </LinkButton>
      {
        showCollapsibleSection && showNames && (
          <ScrollSectionWrapper label={locationNamesLabel} />
        )
      }
    </div>
  )
}

export const AssayDetailFrame = ({
  assayDetails, assayDetailsLastRun, edgeLocations = [], isEdgeEnabled = false 
}) => {
  const baselineStart = assayDetails?.baselineStart;
  const baselineEnd = assayDetails?.baselineEnd;
  const [lastRun, setLastRun] = useState();
  const [nextRun, setNextRun] = useState();
  const { isAssaysV2Enabled } = useClientContext();

  useEffect(() => {
    if (isAssaysV2Enabled) {
      (async () => {
        const times = await getNextRun(assayDetails.id);
        setNextRun(times?.next_run && formatAssayDetailsDatetime(times?.next_run));
        setLastRun(times?.last_run && formatAssayDetailsDatetime(times?.last_run));
      })();
    } else {
      /**
       * @deprecated Assays V1 Logic
       */
      const calcNextRun = calculateNextRun(assayDetails, assayDetailsLastRun, isAssaysV2Enabled);
      if (calcNextRun === '') {
        return ''
      }
      setNextRun(calcNextRun ? formatAssayDetailsDatetime(calcNextRun.toISOString()) : undefined);
      setLastRun(formatAssayDetailsDatetime(assayDetails.lastRun ?? assayDetailsLastRun?.window_start));
    }
  }, [assayDetails, assayDetailsLastRun, isAssaysV2Enabled])

  /**
   * @deprecated Assays V1 Logic
   */
  const splitIopath = isAssaysV2Enabled ? assayDetails?.iopath.split('.') : assayDetails?.iopath?.split(' ')
  const monitoryType = splitIopath?.[0]
  const monitoryTypeCapitalized = monitoryType ? `${monitoryType.charAt(0).toUpperCase()}${monitoryType.slice(1)}` : 'Monitor';
  const namedField = splitIopath?.[1];
  const index = splitIopath?.[2] || 'None';
  const threshold = assayDetails?.alertThreshold;
  const aggType = assayDetails?.summarizerMetadata?.aggregation;
  const binMode = assayDetails?.summarizerMetadata?.binMode;
  const metric = assayDetails?.summarizerMetadata?.metric;
  const numBins = assayDetails?.summarizerMetadata?.numBins;
  const binWeights = assayDetails?.summarizerMetadata?.binWeights;

  const assayLocations = uniq(assayDetails?.locations);
  const hasAllEdges = assayLocations.length >= edgeLocations.length;
  const locationNames = useMemo(() => {
    const matchingLocations = !assayLocations?.length ? [] : assayLocations.map((locationName) => {
      if (locationName === WALLAROO_OPS_CENTER_REGEX) {
        return ({
          id: locationName,
          name: 'Wallaroo Ops Center',
        });
      }
      const location = edgeLocations.find(loc => loc.name === locationName);
      if (!location) {
        return null;
      }

      return location;
    }).filter(location => location !== null);

    if (!matchingLocations) {
      return 'No Edge Locations found for this assay.';
    }
    const sortedLocations = sortEdgeLocations(matchingLocations);
    return sortedLocations.map(({ name }) => name);
  }, [assayLocations, edgeLocations]);

  const binWeightString = (!binWeights || areBinsEquallyWeighted(binWeights)) ? 'Equally Weighted' : 'Provided';

  const baselineStr = useMemo(() => {
    if (!baselineStart || !baselineEnd) {
      return 'Uploaded File';
    }
    return getBaselineRangeString(baselineStart, baselineEnd)
  }, [baselineStart, baselineEnd]);

  return (
    <div className="flex flex-col">
      <div className="grid grid-cols-3 gap-4 mt-4">
        <AssayDetailAttribute label={monitoryTypeCapitalized}>{namedField}</AssayDetailAttribute>
        <AssayDetailAttribute label="Index">{index}</AssayDetailAttribute>
      </div>
      <div className="grid grid-cols-3 gap-4 mt-4">
        <AssayDetailAttribute label="Baseline">{baselineStr}</AssayDetailAttribute>
        <AssayDetailAttribute label="Last Run">{lastRun}</AssayDetailAttribute>
        <AssayDetailAttribute label="Next Run">{nextRun}</AssayDetailAttribute>
        <AssayDetailAttribute label="Aggregation Type">{aggType}</AssayDetailAttribute>
        <AssayDetailAttribute label="Threshold">{threshold}</AssayDetailAttribute>
        <AssayDetailAttribute label="Metric">{metric}</AssayDetailAttribute>
        <AssayDetailAttribute label="Number of Bins">{numBins}</AssayDetailAttribute>
        <AssayDetailAttribute label="Bin Weight">{binWeightString}</AssayDetailAttribute>
        <AssayDetailAttribute label="Bin Mode">{binMode}</AssayDetailAttribute>
      </div>
      {
        isEdgeEnabled && <CollapsibleLocationsSection locationNames={locationNames} hasAllEdges={hasAllEdges} />
      }
    </div>
  )
}

export const AssayDetailModal = ({
  assayDetails,
  assayDetailsLastRun,
  onModalToggle,
  edgeLocations = [],
  isShown = false,
  isEdgeEnabled = false,
}) => {
  const [show, setShow] = useState(isShown);

  const assayName = assayDetails?.name;

  const closeModal = useCallback(() => {
    setShow(false);
    onModalToggle(false)
  }, [onModalToggle])

  useEffect(() => {
    setShow(isShown);
  }, [isShown])

  if (!show) {
    return null;
  }

  return (
    <ModalOverlay animationKey="assay-detail-modal" active={show} onModalToggle={closeModal}>
      <div className="relative">
        <CardView className="flex flex-col justify-evenly relative" disableFullHeight>
          <div className="flex justify-between items-center mb-2">
            <Label size="lg" bold>Details of {assayName}</Label>
            <Button
              variant="close"
              textColor="border-dark"
              hoverTextColor="border-darker"
              activeTextColor="info"
              onClick={closeModal}
            />
          </div>
          <AssayDetailFrame assayDetails={assayDetails} assayDetailsLastRun={assayDetailsLastRun} edgeLocations={edgeLocations} isEdgeEnabled={isEdgeEnabled} />
        </CardView>
      </div>
    </ModalOverlay>
  )
}

export default AssayDetailModal;
